import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, map, Observable, throwError } from 'rxjs';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import moment from 'moment';

import { 
  OverrideEntity, 
  LegalEntity, 
  Classification, 
  TaxConsolidationGroup, 
  ApiResponse, 
  SaveParams,
  DEFAULT_STUBS 
} from '../models/entity-classification.models';

@Injectable({
  providedIn: 'root'
})
export class EntityClassificationActionsService {
  
  private readonly URLS = {
    GET_ONLY_ENTITY: '/loadJsonObject?action_id=32525',
    GET_ENTITY_DETAILS: '/loadJsonObject?action_id=32023',
    GET_CLASSIFICATION: '/loadJsonObject?action_id=32071',
    TAX_CONSOLE_GROUP: '/loadJsonObject?action_id=32433',
    SAVE_CREATE: '/saveJsonObject?action_code=lrcue9',
    SAVE_UPDATE: '/saveJsonObject?action_code=pzl4ye',
    DELETE_ENTITY: '/saveJsonObject?action_code=sq9z1i'
  };

  constructor(
    private http: HttpClient,
    private fb: UntypedFormBuilder
  ) {}

  /**
   * Load all entities for dropdown
   */
  loadAllEntities(baseUrl: string, globalParams: any , filterParams: any): Observable<LegalEntity[]> {
    const url = `${baseUrl}/loadJsonObject`;
    const params: any = {
      action_code: 'p15w5t',
      tax_year: filterParams?.tax_year || globalParams?.tax_year,
      is_locked: 'N',
      issue_id: '',
      issue_id_for_tx_save: '',
      issue_short_desc: '',
      jcd_key: globalParams?.jcd_key || '',
      jcd_ta_key: globalParams?.jcd_ta_key || '',
      screen_key: globalParams?.screen_key || '',
      screen_type: 'grid'
    };
    
    return this.http.get<ApiResponse<LegalEntity[]>>(url, { params })
      .pipe(
        map(response => {
          if (response.callSuccess === '1') {
            return response.jsonObject || [];
          } else {
            throw new Error(response.errorMessage || 'Unable to retrieve entities');
          }
        }),
        catchError(error => {
          console.error('Error loading entities:', error);
          return throwError(() => new Error('Unable to retrieve entities. Please try again.'));
        })
      );
  }

  /**
   * Load all override entities with formatted dates
   */
  loadAllOverrideEntities(baseUrl: string, globalParams: any, filterParams: any): Observable<OverrideEntity[]> {
    const url = `${baseUrl}/loadJsonObject`;
    const params: any = {
      action_id: '32023',
      action_code: 'lvcm1n',
      activity_key: globalParams?.activity_key || filterParams?.activity_key || '',
      is_locked: 'N',
      issue_id: '',
      issue_id_for_tx_save: '',
      issue_short_desc: '',
      jcd_key: globalParams?.jcd_key || filterParams?.jcd_key || '',
      jcd_ta_key: globalParams?.jcd_ta_key || filterParams?.jcd_ta_key || '',
      scenario: globalParams?.scenario || filterParams?.scenario || '',
      scenario_ec: globalParams?.scenario_ec || filterParams?.scenario_ec || '1',
      screen_key: globalParams?.screen_key || filterParams?.screen_key || '',
      screen_type: 'grid',
      tax_year: filterParams?.tax_year || globalParams?.tax_year || ''
    };
    
    return this.http.get<ApiResponse<OverrideEntity[]>>(url, { params })
      .pipe(
        map(response => {
          if (response.callSuccess === '1') {
            const entities = response.jsonObject || [];
            return entities.map(entity => this.formatEntityDates(entity));
          } else {
            throw new Error(response.errorMessage || 'Unable to retrieve override entities');
          }
        }),
        catchError(error => {
          console.error('Error loading override entities:', error);
          return throwError(() => new Error('Unable to retrieve override entities. Please try again.'));
        })
      );
  }

  /**
   * Load all classifications
   */
  loadClassifications(baseUrl: string, globalParams: any, filterParams: any): Observable<Classification[]> {
    const url = `${baseUrl}/loadJsonObject`;
    const params: any = {
      action_code: 'cragj2',
      tax_year: filterParams?.tax_year || globalParams?.tax_year,
      is_locked: 'N',
      issue_id: '',
      issue_id_for_tx_save: '',
      issue_short_desc: '',
      jcd_key: globalParams?.jcd_key || '',
      jcd_ta_key: globalParams?.jcd_ta_key || '',
      screen_key: globalParams?.screen_key || '',
      screen_type: 'grid'
    };
    
    return this.http.get<ApiResponse<Classification[]>>(url, { params })
      .pipe(
        map(response => {
          if (response.callSuccess === '1') {
            return response.jsonObject || [];
          } else {
            throw new Error(response.errorMessage || 'Unable to retrieve classifications');
          }
        }),
        catchError(error => {
          console.error('Error loading classifications:', error);
          return throwError(() => new Error('Unable to retrieve classifications. Please try again.'));
        })
      );
  }

  /**
   * Load tax consolidation groups
   */
  loadTaxConsolidationGroups(baseUrl: string, globalParams: any, filterParams: any): Observable<TaxConsolidationGroup[]> {
    const url = `${baseUrl}/loadJsonObject`;
    const params: any = {
      action_id: '32433',
      action_code: 'r7liui',
      activity_key: globalParams?.activity_key || filterParams?.activity_key || '',
      is_locked: 'N',
      issue_id: '',
      issue_id_for_tx_save: '',
      issue_short_desc: '',
      jcd_key: globalParams?.jcd_key || filterParams?.jcd_key || '',
      jcd_ta_key: globalParams?.jcd_ta_key || filterParams?.jcd_ta_key || '',
      scenario: globalParams?.scenario || filterParams?.scenario || '',
      scenario_ec: globalParams?.scenario_ec || filterParams?.scenario_ec || '1',
      screen_key: globalParams?.screen_key || filterParams?.screen_key || '',
      screen_type: 'grid',
      tax_year: filterParams?.tax_year || globalParams?.tax_year || ''
    };
    
    return this.http.get<ApiResponse<TaxConsolidationGroup[]>>(url, { params })
      .pipe(
        map(response => {
          if (response.callSuccess === '1') {
            return response.jsonObject || [];
          } else {
            throw new Error(response.errorMessage || 'Unable to retrieve tax consolidation groups');
          }
        }),
        catchError(error => {
          console.error('Error loading tax consolidation groups:', error);
          return throwError(() => new Error('Unable to retrieve tax consolidation groups. Please try again.'));
        })
      );
  }

  /**
   * Save entity (create mode)
   */
  saveEntity(baseUrl: string, entity: OverrideEntity, globalParams: any, filterParams: any): Observable<ApiResponse> {
    const formattedEntity = this.formatDateFields(entity);
    const saveParams = this.prepareSaveParams(formattedEntity, globalParams, filterParams, 'REPLACE');
    const url = `${baseUrl}${this.URLS.SAVE_CREATE}`;
    
    return this.postFormEncoded(url, saveParams)
      .pipe(
        catchError(error => {
          console.error('Error saving entity:', error);
          return throwError(() => new Error('Unable to save entity. Please try again.'));
        })
      );
  }

  /**
   * Update entity (update mode)
   */
  updateEntity(baseUrl: string, entity: OverrideEntity, globalParams: any, filterParams: any, isDelete: boolean = false): Observable<ApiResponse> {
    const formattedEntity = this.formatDateFields(entity);
    const editType = isDelete ? 'DELETE' : 'REPLACE';
    const saveParams = this.prepareSaveParams(formattedEntity, globalParams, filterParams, editType);
    const url = `${baseUrl}${this.URLS.SAVE_UPDATE}`;
    
    return this.postFormEncoded(url, saveParams)
      .pipe(
        catchError(error => {
          console.error('Error updating entity:', error);
          return throwError(() => new Error('Unable to update entity. Please try again.'));
        })
      );
  }

  /**
   * Delete entity (remove override)
   */
  deleteEntity(
    baseUrl: string,
    entityId: string,
    stubId: string,
    entity: OverrideEntity,
    globalParams: any,
    filterParams: any
  ): Observable<ApiResponse> {
    const jsonSettings = JSON.stringify({
      tax_year: entity.TAX_YEAR,
      child_id: entityId,
      stub: stubId
    });

    const deleteParams = {
      ...globalParams,
      ...filterParams,
      jsonObj: JSON.stringify(entity),
      jsonSettings,
      tax_year: entity.TAX_YEAR,
      child_id: entityId,
      stub: stubId,
      is_issue_key_enabled: 'N'
    };

    const url = `${baseUrl}${this.URLS.DELETE_ENTITY}`;
    return this.postFormEncoded(url, deleteParams)
      .pipe(
        catchError(error => {
          console.error('Error deleting entity:', error);
          return throwError(() => new Error('Unable to delete entity. Please try again.'));
        })
      );
  }

  private postFormEncoded(url: string, params: Record<string, any>): Observable<ApiResponse> {
    const body = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      const value = params[key];
      if (value !== undefined && value !== null) {
        body.set(key, String(value));
      }
    });
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    return this.http.post<ApiResponse>(url, body.toString(), { headers, withCredentials: true });
  }

  /**
   * Format entity dates from YYYY-MM-DD to MM/DD/YYYY
   */
  private formatEntityDates(entity: OverrideEntity): OverrideEntity {
    const dateFields = [
      'START_DATE', 'END_DATE', 'GOLD_START_DATE', 'GOLD_END_DATE',
      'ACQUISITION_DATE', 'DATE_INCORPORATED', 'DATE_MERGED', 'DATE_DISSOLVED',
      'DATE_SOLD', 'DATE_SPAWNED_CONSUMED', 'DATE_LIQUIDATED'
    ];

    const formattedEntity = { ...entity };

    dateFields.forEach(field => {
      if ((formattedEntity as any)[field]) {
        (formattedEntity as any)[field] = moment((formattedEntity as any)[field], 'YYYY-MM-DD').format('MM/DD/YYYY');
      }
    });

    // Handle RETURN_PREPARED_BY trimming
    if (formattedEntity.RETURN_PREPARED_BY) {
      formattedEntity.RETURN_PREPARED_BY = formattedEntity.RETURN_PREPARED_BY.trim();
    }

    return formattedEntity;
  }

  /**
   * Format date fields for saving (convert to MM/DD/YYYY format)
   */
  formatDateFields(entity: OverrideEntity): OverrideEntity {
    const dateFields = [
      'START_DATE', 'END_DATE', 'GOLD_START_DATE', 'GOLD_END_DATE',
      'ACQUISITION_DATE', 'DATE_INCORPORATED', 'DATE_MERGED', 'DATE_DISSOLVED',
      'DATE_SOLD', 'DATE_SPAWNED_CONSUMED', 'DATE_LIQUIDATED'
    ];

    const formattedEntity = { ...entity };

    dateFields.forEach(field => {
      const fieldValue = (formattedEntity as any)[field];
      if (fieldValue) {
        const m = moment(fieldValue);
        (formattedEntity as any)[field] = m.isValid() ? m.format('MM/DD/YYYY') : '';
      }
    });

    if (formattedEntity.RETURN_PREPARED_BY) {
      formattedEntity.RETURN_PREPARED_BY = formattedEntity.RETURN_PREPARED_BY.trim();
    }

    return formattedEntity;
  }

  /**
   * Prepare save parameters for API call
   */
  private prepareSaveParams(entity: OverrideEntity, globalParams: any, filterParams: any, editType: string): Record<string, any> {
    const entityToSave = { ...entity, EDIT_TYPE: editType };

    const jsonSettings = JSON.stringify({
      tax_year: filterParams?.tax_year ?? globalParams?.tax_year ?? ''
    });

    return {
      ...globalParams,
      ...filterParams,
      jsonObj: JSON.stringify(entityToSave),
      jsonSettings,
      scenarioCode:
        filterParams?.scenarioCode ??
        filterParams?.scenario_code ??
        globalParams?.scenarioCode ??
        globalParams?.scenario_code ??
        'RAF',
      is_issue_key_enabled: 'N'
    };
  }

  /**
   * Set classification description based on selected classification
   */
  setClassificationDescription(entity: OverrideEntity, classifications: Classification[]): void {
    const classification = classifications.find(c => c.CLASSIFICATION === entity.CLASSIFICATION);
    if (classification) {
      entity.CLASSIFICATION_DESC = classification.CLASSIFICATION_DESC;
    }
  }

  /**
   * Set tax consolidation group description based on selected group
   */
  setTaxConsolidationGroupDescription(entity: OverrideEntity, taxGroups: TaxConsolidationGroup[]): void {
    const taxGroup = taxGroups.find(g => g.TAX_CONSOLIDATION_GRP === entity.TAX_CONSOLIDATION_GRP);
    if (taxGroup) {
      entity.TAX_CONSOLIDATION_GRP_DESC = taxGroup.TAX_CONSOLIDATION_GRP_DESC;
    }
  }

  /**
   * Create reactive form with all required fields and validation
   */
  createEntityForm(): UntypedFormGroup {
    return this.fb.group({
      CHILD_ID: ['', [Validators.required]],
      STUB: ['', [Validators.required]],
      START_DATE: ['', [Validators.required]],
      END_DATE: ['', [Validators.required]],
      TAX_TYPE: ['', [Validators.required]],
      CLASSIFICATION: ['', [Validators.required]],
      DATE_INCORPORATED: ['', [Validators.required]],
      TAX_CONSOLIDATION_GRP: ['', [Validators.required]],
      COUNTRY_INCORPORATED: ['', [Validators.required]],
      RETURN_PREPARED_BY: ['', [Validators.required]],
      ENTITY_TYPE: ['', [Validators.required]],
      FILING_REQUIREMENT: ['', [Validators.required]],
      ROLL_FORWARD: ['', [Validators.required]],
      EDIT_COMMENTS: [''],
      LE_SHORT_NAME: [''],
      GECC_ENTITY_ID: [''],
      HO_LEIDS: [''],
      HO_GECC_ENTITY_ID: [''],
      HO_TAX_TYPE: [''],
      HO_COUNTRY_INCORPORATED: [''],
      CLASSIFICATION_DESC: [''],
      ACQUISITION_DATE: [''],
      DATE_SPAWNED_CONSUMED: [''],
      DATE_DISSOLVED: [''],
      DATE_LIQUIDATED: [''],
      DATE_SOLD: [''],
      TAX_CONSOLIDATION_GRP_DESC: [''],
      GOLD_START_DATE: [''],
      GOLD_END_DATE: [''],
      DCS_ENTITY: [''],
      FIR_ENTITY: [''],
      SUB_CONSOL: [''],
      TECH_TERM: [''],
      SINGLE_OWNER_PSHIP: [''],
      PROCESS_NOTES: [''],
      UPDATED: [''],
      DATA_HASH: [''],
      US_HO_LEID: [''],
      US_HO_REPORTING_PERIOD: [''],
      TAX_YEAR: [''],
      SCENARIO: [''],
      EDIT_TYPE: ['REPLACE']
    });
  }

  /**
   * Get default entity object for initialization
   */
  getDefaultEntity(): Partial<OverrideEntity> {
    return {
      EDIT_TYPE: 'REPLACE',
      EDIT_COMMENTS: '',
      CHILD_ID: '',
      STUB: '',
      TAX_YEAR: '',
      SCENARIO: '',
      START_DATE: '',
      END_DATE: '',
      LE_SHORT_NAME: '',
      GECC_ENTITY_ID: '',
      TAX_TYPE: '',
      HO_LEIDS: '',
      HO_GECC_ENTITY_ID: '',
      HO_TAX_TYPE: '',
      HO_COUNTRY_INCORPORATED: '',
      CLASSIFICATION_DESC: '',
      CLASSIFICATION: '',
      ACQUISITION_DATE: '',
      DATE_INCORPORATED: '',
      DATE_SPAWNED_CONSUMED: '',
      DATE_DISSOLVED: '',
      DATE_LIQUIDATED: '',
      DATE_SOLD: '',
      TAX_CONSOLIDATION_GRP: '',
      TAX_CONSOLIDATION_GRP_DESC: '',
      COUNTRY_INCORPORATED: '',
      RETURN_PREPARED_BY: '',
      GOLD_START_DATE: '',
      GOLD_END_DATE: '',
      DCS_ENTITY: '',
      FIR_ENTITY: '',
      SUB_CONSOL: '',
      TECH_TERM: '',
      SINGLE_OWNER_PSHIP: '',
      PROCESS_NOTES: '',
      UPDATED: '',
      DATA_HASH: '',
      FILING_REQUIREMENT: '',
      ROLL_FORWARD: '',
      US_HO_LEID: '',
      US_HO_REPORTING_PERIOD: '',
      ENTITY_TYPE: ''
    };
  }

  /**
   * Validate form and return validation result
   */
  validateForm(form: UntypedFormGroup): boolean {
    if (form.invalid) {
      // Mark all fields as touched to show validation errors
      Object.keys(form.controls).forEach(key => {
        form.get(key)?.markAsTouched();
      });
      return false;
    }
    return true;
  }

  /**
   * Get field error message
   */
  getFieldError(form: UntypedFormGroup, fieldName: string): string | null {
    const field = form.get(fieldName);
    if (field && field.invalid && field.touched) {
      if (field.errors?.['required']) {
        return `Please provide ${this.getFieldLabel(fieldName)}`;
      }
    }
    return null;
  }

  /**
   * Check if field has error
   */
  hasFieldError(form: UntypedFormGroup, fieldName: string): boolean {
    const field = form.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  /**
   * Get field label for error messages
   */
  getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      'CHILD_ID': 'Legal Entity Id',
      'STUB': 'Reporting Period',
      'START_DATE': 'Tax Year Begin Date',
      'END_DATE': 'Tax Year End Date',
      'TAX_TYPE': 'Tax Type',
      'CLASSIFICATION': 'Classification',
      'DATE_INCORPORATED': 'Date Incorporated',
      'TAX_CONSOLIDATION_GRP': 'Tax Consolidation Group',
      'COUNTRY_INCORPORATED': 'Country Incorporated',
      'RETURN_PREPARED_BY': 'Return Prepared By',
      'ENTITY_TYPE': 'Entity Type',
      'FILING_REQUIREMENT': 'Filing Requirement',
      'ROLL_FORWARD': 'Roll Forward to next year'
    };
    return labels[fieldName] || fieldName;
  }

  /**
   * Handle entity selection change for create mode
   */
  handleEntitySelectionChange(
    form: UntypedFormGroup, 
    childId: string, 
    allEntities: LegalEntity[]
  ): void {
    if (!childId) return;

    const selectedEntity = allEntities.find(entity => entity.CHILD_ID === childId);
    if (selectedEntity) {
      form.patchValue(selectedEntity);
      
      // Handle US_HO_REPORTING_PERIOD formatting
      if (selectedEntity.US_HO_REPORTING_PERIOD == null || 
          selectedEntity.US_HO_REPORTING_PERIOD === undefined || 
          selectedEntity.US_HO_REPORTING_PERIOD === '') {
        form.patchValue({ US_HO_REPORTING_PERIOD: '' });
      } else {
        form.patchValue({ 
          US_HO_REPORTING_PERIOD: selectedEntity.US_HO_REPORTING_PERIOD.toString() 
        });
      }

      // Set RETURN_PREPARED_BY based on the original logic
      if (selectedEntity.RETURN_PREPARED_BY === 'C') {
        form.patchValue({ RETURN_PREPARED_BY: 'Corporate' });
      } else if (selectedEntity.RETURN_PREPARED_BY === 'E') {
        form.patchValue({ RETURN_PREPARED_BY: 'External' });
      }
    }
  }

  /**
   * Handle tax consolidation group change
   */
  handleTaxConsolidationGroupChange(
    form: UntypedFormGroup, 
    taxGroup: string, 
    allTaxGroups: TaxConsolidationGroup[]
  ): void {
    if (!taxGroup) return;

    const selectedGroup = allTaxGroups.find(g => g.TAX_CONSOLIDATION_GRP === taxGroup);
    if (selectedGroup) {
      form.patchValue({ 
        TAX_CONSOLIDATION_GRP_DESC: selectedGroup.TAX_CONSOLIDATION_GRP_DESC 
      });
    }
  }

  /**
   * Handle classification change
   */
  handleClassificationChange(
    form: UntypedFormGroup, 
    classification: string, 
    allClassifications: Classification[]
  ): void {
    if (!classification) return;

    const selectedClassification = allClassifications.find(c => c.CLASSIFICATION === classification);
    if (selectedClassification) {
      form.patchValue({ 
        CLASSIFICATION_DESC: selectedClassification.CLASSIFICATION_DESC 
      });
    }
  }

  /**
   * Apply legacy master-LEID normalization before populating the form.
   */
  normalizeMasterLeidEntity(entity: OverrideEntity): OverrideEntity {
    const patch: any = { ...entity };
    if (
      patch.US_HO_REPORTING_PERIOD == null ||
      patch.US_HO_REPORTING_PERIOD === undefined ||
      patch.US_HO_REPORTING_PERIOD === ''
    ) {
      patch.US_HO_REPORTING_PERIOD = '';
    } else {
      patch.US_HO_REPORTING_PERIOD = String(patch.US_HO_REPORTING_PERIOD);
    }
    if (patch.RETURN_PREPARED_BY === 'C') {
      patch.RETURN_PREPARED_BY = 'Corporate';
    } else if (patch.RETURN_PREPARED_BY === 'E') {
      patch.RETURN_PREPARED_BY = 'External';
    }
    patch.ENTITY_TYPE = patch.DCS_ENTITY === 'Y' ? 'D' : 'F';
    if (patch.STUB != null && patch.STUB !== '') {
      patch.STUB = String(patch.STUB);
    }
    return patch;
  }

  /**
   * Populate form with entity data for update mode
   */
  populateFormWithEntity(form: UntypedFormGroup, entity: OverrideEntity): void {
    const formData = { ...entity };
    
    // Convert STUB to string for form compatibility
    if (formData.STUB) {
      formData.STUB = formData.STUB.toString();
    }
    
    // Handle US_HO_REPORTING_PERIOD
    if (formData.US_HO_REPORTING_PERIOD == null || 
        formData.US_HO_REPORTING_PERIOD === undefined || 
        formData.US_HO_REPORTING_PERIOD === '') {
      formData.US_HO_REPORTING_PERIOD = '';
    } else {
      formData.US_HO_REPORTING_PERIOD = formData.US_HO_REPORTING_PERIOD.toString();
    }

    // Format dates for HTML date inputs (YYYY-MM-DD format)
    const dateFields = [
      'START_DATE', 'END_DATE', 'ACQUISITION_DATE', 'DATE_INCORPORATED', 
      'DATE_SPAWNED_CONSUMED', 'DATE_DISSOLVED', 'DATE_LIQUIDATED', 'DATE_SOLD',
      'GOLD_START_DATE', 'GOLD_END_DATE'
    ];

    dateFields.forEach(field => {
      const fieldValue = (formData as any)[field];
      if (fieldValue) {
        const m = moment(fieldValue, ['MM/DD/YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
        if (m.isValid()) {
          (formData as any)[field] = m.format('YYYY-MM-DD');
        } else {
          const date = new Date(fieldValue);
          (formData as any)[field] = !isNaN(date.getTime()) ? this.formatDateForInput(date) : '';
        }
      } else {
        (formData as any)[field] = '';
      }
    });

    form.patchValue(formData);
  }

  /**
   * Format date for HTML date input (YYYY-MM-DD format)
   */
  private formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Disable fields for update mode
   */
  disableFieldsForUpdateMode(form: UntypedFormGroup): void {
    form.get('CHILD_ID')?.disable();
    form.get('STUB')?.disable();
    form.get('START_DATE')?.disable();
    form.get('END_DATE')?.disable();
  }

  /**
   * Prepare entity data for save/update operations
   */
  prepareEntityForSave(
    formValue: any, 
    classifications: Classification[], 
    taxGroups: TaxConsolidationGroup[]
  ): OverrideEntity {
    const entity = { ...formValue };
    
    // Set descriptions
    this.setClassificationDescription(entity, classifications);
    this.setTaxConsolidationGroupDescription(entity, taxGroups);
    
    return entity;
  }

  /**
   * Get available reporting periods (stubs)
   */
  getReportingPeriods() {
    return DEFAULT_STUBS;
  }

  /**
   * Track by function for ngFor performance optimization
   */
  trackByIndex(index: number, item: any): number {
    return index;
  }

  /**
   * Track by entity ID for ngFor performance
   */
  trackByEntityId(index: number, entity: LegalEntity): string {
    return entity.CHILD_ID;
  }
}